
import * as React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Analytics } from "./components/seo/Analytics";
import { GuestDataMigration } from "./components/auth/GuestDataMigration";
import { LottieLoader } from "@/components/ui/lottie-loader";
import { TrackingProvider } from "./components/tracking/TrackingProvider";

// Eager load homepage for fast LCP
import Homepage2 from "./pages/Homepage2";

// Lazy load other pages for code splitting
const Index = React.lazy(() => import("./pages/Index"));
const Interview = React.lazy(() => import("./pages/Interview"));
const Auth = React.lazy(() => import("./pages/Auth"));
const AuthCallback = React.lazy(() => import("./pages/auth/AuthCallback"));
const CaseStudies = React.lazy(() => import("./pages/CaseStudies"));
const ResumeBuilder = React.lazy(() => import("./pages/ResumeBuilder"));
const FeedbackTest = React.lazy(() => import("./pages/FeedbackTest"));
const PasswordReset = React.lazy(() => import("./components/auth/PasswordReset"));
const TermsAndConditions = React.lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = React.lazy(() => import("./pages/RefundPolicy"));
const Profile = React.lazy(() => import("./pages/Profile"));
const Settings = React.lazy(() => import("./pages/Settings"));
const TestLanding = React.lazy(() => import("./pages/TestLanding"));
const Blog = React.lazy(() => import("./pages/Blog"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const Careers = React.lazy(() => import("./pages/Careers"));
const JobDetails = React.lazy(() => import("./pages/JobDetails"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const Pricing = React.lazy(() => import("./pages/Pricing"));
const MarketingDashboard = React.lazy(() => import("./pages/MarketingDashboard"));
const CareerDiscovery = React.lazy(() => import("./pages/CareerDiscovery"));
const IntroFlow = React.lazy(() => import("./components/career/intro/IntroFlow").then(m => ({ default: m.IntroFlow })));
const IntroRedirect = React.lazy(() => import("./components/career/intro/IntroRedirect").then(m => ({ default: m.IntroRedirect })));
const AssessmentFlow = React.lazy(() => import("./components/career/AssessmentFlow"));
const EnhancedResultsDashboard = React.lazy(() => import("./components/career/EnhancedResultsDashboard"));
const HomePage = React.lazy(() => import("./pages/HomePage"));
const Home = React.lazy(() => import("./pages/Home"));
const JobHunt = React.lazy(() => import("./pages/JobHunt"));
const AffiliateRegister = React.lazy(() => import("./pages/affiliate/AffiliateRegister"));
const AffiliateDashboard = React.lazy(() => import("./pages/affiliate/AffiliateDashboard"));
const InstitutionLanding = React.lazy(() => import("./pages/institution/InstitutionLanding"));
const InstitutionDashboard = React.lazy(() => import("./pages/institution/InstitutionDashboard"));
const InstitutionTermsOfService = React.lazy(() => import("./pages/institution/InstitutionTermsOfService"));
const InstitutionPrivacyPolicy = React.lazy(() => import("./pages/institution/InstitutionPrivacyPolicy"));
const InstitutionManagement = React.lazy(() => import("./pages/admin/InstitutionManagement"));
const ForInstitutions = React.lazy(() => import("./pages/ForInstitutions"));
const SalesTraining = React.lazy(() => import("./pages/SalesTraining"));
const RecruitmentAgencyRegister = React.lazy(() => import("./pages/partner/RecruitmentAgencyRegister"));
const InterviewLanding = React.lazy(() => import("./pages/InterviewLanding"));
const NotFoundPage = React.lazy(() => import("./pages/404"));
const ChromeExtension = React.lazy(() => import("./pages/ChromeExtension"));

// SEO Hub & Spoke Pages - Interview Preparation
const InterviewPreparationHub = React.lazy(() => import("./pages/InterviewPreparationHub"));
const InterviewQuestionsHub = React.lazy(() => import("./pages/InterviewQuestionsHub"));
const JavaQuestionsPage = React.lazy(() => import("./pages/interview-questions/JavaQuestionsPage"));
const PythonQuestionsPage = React.lazy(() => import("./pages/interview-questions/PythonQuestionsPage"));
const SQLQuestionsPage = React.lazy(() => import("./pages/interview-questions/SQLQuestionsPage"));
const HRQuestionsPage = React.lazy(() => import("./pages/interview-questions/HRQuestionsPage"));
const AIInterviewGuide = React.lazy(() => import("./pages/interview-preparation/AIInterviewGuide"));
const SelfIntroductionGuide = React.lazy(() => import("./pages/interview-preparation/SelfIntroductionGuide"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      retry: 1,
      refetchOnWindowFocus: false, // Prevent refetch on window focus
      refetchOnReconnect: false, // Prevent refetch on reconnect
      refetchOnMount: false // Prevent refetch on component mount
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SubscriptionProvider>
          <TooltipProvider>
            <Toaster />
            <HelmetProvider>
              <Router>
                <TrackingProvider>
                  <Analytics />
                  <GuestDataMigration />
                  <Layout>
                <React.Suspense fallback={<LottieLoader />}>
                  <Routes>
                    {/* Homepage loads immediately for fast LCP */}
                    <Route path="/" element={<Homepage2 />} />
                    <Route path="/homepage2" element={<Homepage2 />} />
                    
                    {/* Other routes lazy load */}
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/auth/reset-password" element={<PasswordReset />} />
                    <Route path="/test-landing" element={<TestLanding />} />
                    <Route path="/home-page" element={<HomePage />} />
                    <Route path="/home" element={
                      <ProtectedRoute>
                        <Home />
                      </ProtectedRoute>
                    } />
                    <Route path="/case-studies" element={<CaseStudies />} />
                    <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/refund-policy" element={<RefundPolicy />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/interview" element={<Interview />} />
                    <Route path="/resume-builder/*" element={<ResumeBuilder />} />
                    <Route path="/feedback-test" element={<FeedbackTest />} />
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <Settings />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/careers" element={<Careers />} />
                    <Route path="/careers/:id" element={<JobDetails />} />
                    <Route 
                      path="/admin" 
                      element={
                        <ProtectedRoute>
                          <AdminDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/applications" 
                      element={
                        <ProtectedRoute>
                          <AdminDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/admin/marketing" 
                      element={
                        <ProtectedRoute>
                          <MarketingDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="/career-discovery" element={<CareerDiscovery />} />
                    <Route path="/career-discovery/intro" element={<IntroRedirect />} />
                    <Route path="/career-discovery/intro/:sessionToken" element={<IntroFlow />} />
                    <Route path="/career-discovery/assessment/:sessionToken" element={<AssessmentFlow />} />
                    <Route path="/career-discovery/results/:sessionToken" element={<EnhancedResultsDashboard />} />
                    <Route path="/job-hunt/*" 
                      element={
                        <ProtectedRoute>
                          <JobHunt />
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="/affiliate/register" element={<AffiliateRegister />} />
                    <Route path="/affiliate/dashboard" element={<AffiliateDashboard />} />
                    <Route path="/partner/recruitment-agencies" element={<RecruitmentAgencyRegister />} />
                    <Route path="/chrome-extension" element={<ChromeExtension />} />

                    {/* Institution Routes */}
                    <Route path="/for-institutions" element={<ForInstitutions />} />
                    <Route 
                      path="/sales-training" 
                      element={
                        <ProtectedRoute>
                          <SalesTraining />
                        </ProtectedRoute>
                      } 
                    />
                    <Route path="/i/:slug" element={<InstitutionLanding />} />
                    <Route path="/institution/terms-of-service" element={<InstitutionTermsOfService />} />
                    <Route path="/institution/privacy-policy" element={<InstitutionPrivacyPolicy />} />
                    <Route 
                      path="/institution/dashboard" 
                      element={
                        <ProtectedRoute>
                          <InstitutionDashboard />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Admin Institution Management */}
                    <Route 
                      path="/admin/institutions" 
                      element={
                        <ProtectedRoute>
                          <InstitutionManagement />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* SEO Hub & Spoke - Interview Preparation */}
                    <Route path="/interview-preparation" element={<InterviewPreparationHub />} />
                    <Route path="/interview-preparation/ai-interview-tips" element={<AIInterviewGuide />} />
                    <Route path="/interview-preparation/self-introduction" element={<SelfIntroductionGuide />} />
                    <Route path="/interview-questions" element={<InterviewQuestionsHub />} />
                    <Route path="/interview-questions/java" element={<JavaQuestionsPage />} />
                    <Route path="/interview-questions/python" element={<PythonQuestionsPage />} />
                    <Route path="/interview-questions/sql" element={<SQLQuestionsPage />} />
                    <Route path="/interview-questions/hr" element={<HRQuestionsPage />} />
                    
                    {/* CRO Landing Page for Meta Ads */}
                    <Route path="/try-interview" element={<InterviewLanding />} />
                    
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </React.Suspense>
                  </Layout>
                </TrackingProvider>
              </Router>
            </HelmetProvider>
          </TooltipProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
